import { useMemo } from "react";
import { components, type PlaceholderProps, type GroupBase } from "react-select";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as TriangleAlert } from "assets/images/iconsV2/triangle-alert.svg";
import { classNames } from "helpers/commonHelpers";
import type { SelectOption } from "components/v2/Select/types";
import PackingListSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListSelect";
import { getPackingListFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import { useStopSelectOptions } from "@RequestFormV2/sections/PackingList/utils/stopSelectOptions";
import { isPackingListStopKeyMissing } from "@RequestFormV2/sections/PackingList/utils/getMissingStopWarning";

import "./PackingListStopSelect.scss";

type PackingListStopSelectProps = {
  index: number;
  fieldId: string;
  type: "pickup" | "delivery";
};

function RemovedStopPlaceholder({
  removedLabel,
  ...props
}: PlaceholderProps<SelectOption, false, GroupBase<SelectOption>> & { removedLabel: string }) {
  return (
    <components.Placeholder {...props}>
      <span className="rfv2-packing-list-stop-select__removed">
        <span className="rfv2-packing-list-stop-select__removed-icon-badge">
          <TriangleAlert className="rfv2-packing-list-stop-select__removed-icon" />
        </span>
        {removedLabel}
      </span>
    </components.Placeholder>
  );
}

export default function PackingListStopSelect({ index, fieldId, type }: PackingListStopSelectProps) {
  const intl = useIntl();
  const { pickupOptions, deliveryOptions, formatStopOption } = useStopSelectOptions();
  const { handlePackingListFieldBlur } = usePackingList();

  const isPickup = type === "pickup";
  const options = isPickup ? pickupOptions : deliveryOptions;
  const field = isPickup ? "pickup_stop_key" : "delivery_stop_key";
  const fieldPath = getPackingListFieldPath(index, field);
  const stopKey = useWatch({ name: fieldPath }) as string | undefined;

  const isStopRemoved = isPackingListStopKeyMissing(stopKey, options);

  const selectStopPlaceholder = intl.formatMessage({
    id: "request-form.packing-list.selectStop",
    defaultMessage: "Select stop",
  });

  const stopWasRemovedLabel = intl.formatMessage({
    id: "request-form.packing-list.stopWasRemoved",
    defaultMessage: "Stop was removed",
  });

  const selectComponents = useMemo(() => {
    if (!isStopRemoved) return undefined;

    return {
      Placeholder: (props: PlaceholderProps<SelectOption, false, GroupBase<SelectOption>>) => (
        <RemovedStopPlaceholder {...props} removedLabel={stopWasRemovedLabel} />
      ),
    };
  }, [isStopRemoved, stopWasRemovedLabel]);

  return (
    <PackingListSelect
      size="sm"
      isClearable
      isSearchable={false}
      name={fieldPath}
      options={options}
      formatOptionLabel={formatStopOption}
      placeholder={isStopRemoved ? stopWasRemovedLabel : selectStopPlaceholder}
      className={classNames({ "rfv2-packing-list-stop-select--removed": isStopRemoved })}
      components={selectComponents}
      onChange={() => handlePackingListFieldBlur(fieldId)}
    />
  );
}
